import type {
  AvailabilitySlot,
  BrowserSchedulingContext,
  ParticipantSchedulingContext,
  PlatformAdminSchedulingContext,
} from './types';
import type { MemberBiweeklyBookingContext } from '@/types/memberBiweeklyBooking';
import type { MemberChemistryBookingContext } from '@/types/memberChemistryBooking';

type LegacyBookingContext = MemberBiweeklyBookingContext &
  MemberChemistryBookingContext;

const toLegacySeatType = (seatType: BrowserSchedulingContext['seatType']) =>
  seatType === 'executive' ? 'executive' : 'standard_leadership';

const DAY_MS = 86_400_000;

/**
 * MEN-5428: the coach-conflicting session starts for one slot, limited to the
 * sessions the series will actually contain. Moments checks up to six
 * occurrences; Rails can shorten the series afterwards, and a date outside it
 * must never appear in the member's note.
 */
export const slotCoachConflictStarts = (
  slot: AvailabilitySlot,
  appointmentCount: number | null | undefined,
  cadenceWeeks: number | null | undefined,
): string[] => {
  const starts = slot.coachConflict?.conflictingOccurrenceStarts ?? [];
  if (!appointmentCount || !cadenceWeeks) return starts;
  const anchor = Date.parse(slot.startAt);
  const step = cadenceWeeks * 7 * DAY_MS;
  return starts.filter(
    (start) =>
      Math.round((Date.parse(start) - anchor) / step) < appointmentCount,
  );
};

export const toLegacyMemberBookingContext = (
  context:
    | BrowserSchedulingContext
    | ParticipantSchedulingContext
    | PlatformAdminSchedulingContext
    | LegacyBookingContext,
): LegacyBookingContext => {
  if (!('contractVersion' in context)) return context;

  return {
    activeSeries: context.activeSeries
      ? {
          bookingSource: 'moments',
          nextAppointmentId: context.activeSeries.nextAppointmentId,
          nextStart: context.activeSeries.nextSchedule.startAt,
          seriesId: context.activeSeries.id,
          sessionCount: context.activeSeries.remainingAppointmentCount,
          time: context.activeSeries.nextSchedule.startAt,
          timezone: context.activeSeries.nextSchedule.timezone,
          weekday: context.activeSeries.nextSchedule.startAt,
        }
      : null,
    availability: {
      days: context.availability.days.map((day) => ({
        date: day.date,
        enabled: day.enabled,
        sessionCount: day.appointmentCount ?? undefined,
        slots: day.slots.map((slot) => {
          const coachConflictStarts = slotCoachConflictStarts(
            slot,
            day.appointmentCount,
            context.cadenceWeeks,
          );
          return {
            ...(coachConflictStarts.length > 0 ? { coachConflictStarts } : {}),
            endTime: slot.endAt,
            label: slot.label,
            startTime: slot.startAt,
          };
        }),
      })),
    },
    bookingSource: 'moments',
    coach: {
      avatarUrl: 'avatarUrl' in context.coach ? context.coach.avatarUrl : null,
      fullName: context.coach.displayName,
      id:
        'personId' in context.coach ? context.coach.personId : context.coach.id,
    },
    durationMinutes: context.durationMinutes,
    hasActiveSeries: context.activeSeries !== null,
    member: {
      email: context.member.email,
      fullName: context.member.displayName,
      id:
        'personId' in context.member
          ? context.member.personId
          : context.member.id,
    },
    seatType: toLegacySeatType(context.seatType),
    sessionCount: context.appointmentCount,
    timezone: context.timezone,
    visibleWeeks: context.visibleWeeks,
  };
};
